import _ from 'underscore';
import { BaseSchemaProcessor } from './base-schema-processor';
import { mapEnumValueForFilter, updateDeliveryStatusPropertyForFilter } from '../util/update-delivery-status-property-for-filter';
import { updateAuctionInsightPropertyForFilter } from '../util/update-auction-insight-property-for-filter';
import { addCampaignTypePropertyForFilter } from '../util/update-campaign-type-property-for-filter';
import { updateStatusPropertyForFilter } from '../util/update-status-property-for-filter';

export class AdGroupSchemaProcessor extends BaseSchemaProcessor {
  updateEntitySchemaForFeatures(entitySchema) {
    const updatedSchema = super.updateEntitySchemaForFeatures(entitySchema);
    // update campaign title to 'campaign name' for adgroup filter, also
    // update the name to 'campaign' to avoid conflict with name for adgroup
    const campaignNameProperty = updatedSchema.properties.CampaignName;

    campaignNameProperty.title = this.i18n.getString('FilterLocalization_DataAttribute_CampaignName');

    // update enum mapping for status
    const statusProperty = updatedSchema.properties.Status;

    const statusEnumSet = [
      'CampaignUserPaused',
      'CampaignBudgetPaused',
      'CampaignBudgetUserPaused',
      'Draft',
      'Active',
      'Paused',
      'Expired'];
    statusProperty.enum = _.map(statusEnumSet, value => mapEnumValueForFilter('AdGroupStatus', value));

    // leave only SearchBid or CpcBid
    const {
      IsManualCpmEnabled,
      IsVideoAdsForCampaignEnabled,
    } = this.Permissions;
    const cpcbid = updatedSchema.properties.CpcBid;
    if (cpcbid) {
      cpcbid.filterable = false;
    }
    if (IsManualCpmEnabled || IsVideoAdsForCampaignEnabled) {
      const searchBid = updatedSchema.properties.SearchBid;
      searchBid.title = this.i18n.getString('AdGroupSummary_ColumnName_CpcBid');
    }
    if (!IsManualCpmEnabled) {
      const cpmbid = updatedSchema.properties.CpmBid;
      if (cpmbid) {
        cpmbid.filterable = false;
      }
    }
    if (!IsVideoAdsForCampaignEnabled && !(this.Permissions.dynamic && this.Permissions.dynamic.XandrVideoCampaigns)) {
      const cpvbid = updatedSchema.properties.CpvBid;
      if (cpvbid) {
        cpvbid.filterable = false;
      }
    }

    if (this.Permissions.dynamic && (this.Permissions.dynamic.XandrVideoCampaigns || this.Permissions.dynamic.XandrDisplayAdsCampaigns)) {
      const biddingStrategyType = updatedSchema.properties.DisplayBiddingStrategyType;
      if (!biddingStrategyType.localized) {
        biddingStrategyType.localized = {};
      }

      biddingStrategyType.localized["Enum.BiddingStrategyType'ManualCpm'"] = this.i18n.getString('BidStrategy_ManualCpm_ViewableCpm');

      if (this.Permissions.dynamic.XandrVideoCampaigns) {
        biddingStrategyType.localized["Enum.BiddingStrategyType'ManualCpv'"] = this.i18n.getString('BidStrategy_ManualCpv_Cpcv');
      }
    }

    // update adgroup title to 'ad group name'
    const adgroupNameProperty = updatedSchema.properties.Name;

    adgroupNameProperty.title = this.i18n.getString('FilterLocalization_DataAttribute_AdGroupName');

    const adgroupLanguageProperty = updatedSchema.properties.Language;

    if (adgroupLanguageProperty && adgroupLanguageProperty.filterable) {
      adgroupLanguageProperty.enum = _.without(adgroupLanguageProperty.enum, "Enum.Language'All'");
      const {
        IsSimplifiedChineseLanguageEnabledInMultipleLanguage,
        IsAdsGlobalizationPhase1Enabled,
        IsAdsGlobalizationPhase2Enabled,
        IsAdsGlobalizationPhase2JapanEnabled,
        IsAdsGlobalizationPhase6Enabled,
        IsAdsGlobalizationPhase9Enabled,
        IsAdsGlobalizationPhase9VIEnabled,
      } = this.Permissions;

      if (!IsSimplifiedChineseLanguageEnabledInMultipleLanguage) {
        adgroupLanguageProperty.enum = _.without(adgroupLanguageProperty.enum, "Enum.Language'TraditionalChinese'");
      }

      if (!IsAdsGlobalizationPhase1Enabled) {
        adgroupLanguageProperty.enum = _.without(
          adgroupLanguageProperty.enum,
          "Enum.Language'Greek'",
          "Enum.Language'Polish'",
          "Enum.Language'Bulgarian'",
          "Enum.Language'Croatian'",
          "Enum.Language'Czech'",
          "Enum.Language'Estonian'",
          "Enum.Language'Hungarian'",
          "Enum.Language'Latvian'",
          "Enum.Language'Lithuanian'",
          "Enum.Language'Maltese'",
          "Enum.Language'Romanian'",
          "Enum.Language'Slovak'",
          "Enum.Language'Slovenian'",
          "Enum.Language'Turkish'",
          "Enum.Language'Serbian'",
          "Enum.Language'Bosnian'",
          "Enum.Language'Albanian'",
          "Enum.Language'Macedonian'",
          "Enum.Language'Icelandic'"
        );
      }

      if (!IsAdsGlobalizationPhase2Enabled) {
        adgroupLanguageProperty.enum = _.without(
          adgroupLanguageProperty.enum,
          "Enum.Language'Turkish'",
          "Enum.Language'Serbian'",
          "Enum.Language'Bosnian'",
          "Enum.Language'Albanian'",
          "Enum.Language'Macedonian'",
          "Enum.Language'Icelandic'"
        );
      }

      if (!IsAdsGlobalizationPhase2JapanEnabled) {
        adgroupLanguageProperty.enum = _.without(
          adgroupLanguageProperty.enum,
          "Enum.Language'Japanese'"
        );
      }

      if (!IsAdsGlobalizationPhase6Enabled) {
        adgroupLanguageProperty.enum = _.without(
          adgroupLanguageProperty.enum,
          "Enum.Language'Hebrew'",
          "Enum.Language'Arabic'",
          "Enum.Language'Russian'"
        );
      }

      if (!IsAdsGlobalizationPhase9Enabled) {
        adgroupLanguageProperty.enum = _.without(
          adgroupLanguageProperty.enum,
          "Enum.Language'Hindi'",
          "Enum.Language'Malay'",
          "Enum.Language'Thai'",
          "Enum.Language'Indonesian'",
          "Enum.Language'Tagalog'"
        );
      }

      if (!IsAdsGlobalizationPhase9VIEnabled) {
        adgroupLanguageProperty.enum = _.without(
          adgroupLanguageProperty.enum,
          "Enum.Language'Vietnamese'"
        );
      }

      adgroupLanguageProperty.enum.unshift(null);
      adgroupLanguageProperty.localized = {
        null: this.i18n.getString('Language_Inherit'),
      };
    }

    const deliveryStatusEnumSet = [
      'InvalidFinancialStatusOther',
      'CampaignSuspended',
      'CampaignBudgetPaused',
      'CampaignUserPaused',
      'Eligible',
      'AdGroupExpired',
      'AdGroupDraft',
      'AdGroupUserPaused',
      'AdGroupPrivacyPending',
      'AdGroupTargetingTooNarrow',
      'NoEligibleAds',
      'NoEligibleKeywords',
    ];

    if (this.Permissions && this.Permissions.dynamic && this.Permissions.dynamic.CampaignLifetimeBudget) {
      deliveryStatusEnumSet.push('CampaignExpired');
    }

    updateStatusPropertyForFilter(entitySchema);
    updateDeliveryStatusPropertyForFilter(entitySchema, deliveryStatusEnumSet);
    updateAuctionInsightPropertyForFilter(entitySchema);

    // make campaignstatus not show in filter drop down
    const campaignStatusProperty = updatedSchema.properties.CampaignStatus;

    if (campaignStatusProperty) {
      campaignStatusProperty.filterable = false;
    }

    if (this.isForVNext) {
      const campaignTypeNotSupported = ["Enum.CampaignType'PerformanceMax'"];
      addCampaignTypePropertyForFilter({
        entitySchema: updatedSchema, permissions: this.Permissions,
      });
      updatedSchema.properties.CampaignType.enum =
      _.difference(updatedSchema.properties.CampaignType.enum, campaignTypeNotSupported);
      // These properties are not supported by google.
      // For first iteration, we will drop their support.
      delete updatedSchema.properties.StartDate;
      delete updatedSchema.properties.EndDate;
      delete updatedSchema.properties.Language;
    }

    // automated rule will not work if wrap below in if (isVNext)
    if (this.Permissions.IsPersonalizedOffersEnabled) {
      updatedSchema.properties.AdGroupCashbackPercent = {
        filterable: true,
        type: 'number',
        minimum: 0,
        maximum: 50,
        property: 'AdGroupCashback/Percent',
        required: true,
      };
      updatedSchema.properties.AdGroupCashbackScope = {
        filterable: true,
        type: 'string',
        property: 'AdGroupCashback/Scope',
        chooseType: 'checkbox',
        operators: ['eq'],
        enum: ["Enum.CashbackScope'None'", "Enum.CashbackScope'Store'", "Enum.CashbackScope'Product'"],
        enumValueMap: {
          "Enum.CashbackScope'None'": 'None',
          "Enum.CashbackScope'Store'": 'Store',
          "Enum.CashbackScope'Product'": 'Product',
        },
        required: true,
      };
      if (this.Permissions.IsPersonalizedOffersV3Enabled) {
        updatedSchema.properties.Coupon = {
          filterable: true,
          type: 'string',
          property: 'AdGroupCashback/CouponsEnabled',
          chooseType: 'checkbox',
          operators: ['in'],
          enum: ["Enum.CouponsEnabled'None'", "Enum.CouponsEnabled'Enabled'", "Enum.CouponsEnabled'Disabled'"],
          enumValueMap: {
            "Enum.CouponsEnabled'None'": 'None',
            "Enum.CouponsEnabled'Enabled'": 'Enabled',
            "Enum.CouponsEnabled'Disabled'": 'Disabled',
          },
          required: true,
        };
      }
    }

    if (!this.Permissions.IsDSAMixedModeCampaignEnabled) {
      delete updatedSchema.properties.AdGroupType;
    } else {
      const adGroupTypeProperty = updatedSchema.properties.AdGroupType;

      adGroupTypeProperty.chooseType = 'checkbox';
      adGroupTypeProperty.operators = ['eq'];
      adGroupTypeProperty.enum = _.without(adGroupTypeProperty.enum, "Enum.AdGroupType'Unknown'", "Enum.AdGroupType'HotelAds'");
      adGroupTypeProperty.enum.push(null);
      adGroupTypeProperty.localized = {
        null: '-',
      };
    }

    return updatedSchema;
  }

  updateOdataOptions(odataOptions) {
    super.updateOdataOptions(odataOptions);

    _.extendOwn(odataOptions.custom, {
      AdGroupType: {
        eq: (filterValues) => {
          let queryStr = _.map(filterValues, value => `AdGroupType eq ${value}`).join(' or ');

          if (this.Permissions.dynamic.HotelCampaign && filterValues.includes(null)) {
            queryStr += " or AdGroupType eq Enum.AdGroupType'HotelAds'";
          }

          return `(${queryStr})`;
        },
      },
      'AdGroupCashback/Scope': {
        eq: (filterValues) => {
          const queryStr = _.map(filterValues, value => `AdGroupCashback/Scope eq ${value}`).join(' or ');

          return `(${queryStr})`;
        },
      },
    });

    return odataOptions;
  }
}
